<script setup>
import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import { VisionAiButton as DashingVisionAiButton } from '@dashhudson/dashing-ui/vision-ai-button';
import { computed, isRef } from 'vue';

import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import {
  INTENT_TYPES,
  REGENERATE_TOOLTIPS,
  SUMMARY_INTENT_TYPES,
  VISION_AI_SUMMARY_LAUNCH_TYPE,
} from '@/components/VisionAi/constants';
import { aiSummaryLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { useFlagStore } from '@/stores/flag';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiMessagesStore = useVisionAiMessagesStore();
const flagStore = useFlagStore();
const filterSidePanel = useFilterSidePanel();
const pageFilters = computed(() => {
  const f = filterSidePanel?.scopedFilters ?? filterSidePanel?.filters;
  if (!f) return {};
  const raw = isRef(f) ? f.value : f;
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? { ...raw } : {};
});
const props = defineProps({
  large: {
    type: Boolean,
    default: false,
  },
  openAutoExpanded: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: 'Summarize',
  },
  buttonIdentifier: {
    type: String,
    default: undefined,
  },
  /** Optional extra context for request_context.extra (e.g. { messageId } for thread). */
  extra: {
    type: Object,
    default: undefined,
  },
  /** Override intent type (e.g. INTENT_TYPES.UNIFIED for summarization buttons). */
  intentTypeOverride: {
    type: String,
    default: undefined,
  },
  /** Override first message sent when starting conversation (e.g. "Summarize" for insights). */
  defaultPrompt: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['openChatWindow']);

const { checkIfInvalidThreshold, getTooltip } = useIntentMap();

const tooltip = computed(() =>
  visionAiMessagesStore.summaryLoading
    ? REGENERATE_TOOLTIPS.LOADING
    : getTooltip(visionAiStore.currentIntentType),
);

async function handleButtonClicked(event) {
  visionAiStore.openChatWindow();
  if (flagStore.flags.visionAiStreaming && props.openAutoExpanded) {
    visionAiStore.expandChatPopup();
  }

  if (!visionAiConversationsStore.summariesSelected) {
    visionAiConversationsStore.selectSummaries();
    visionAiStore.clearLocalChatHistory();
    await visionAiMessagesStore.getMessages({ intentTypes: SUMMARY_INTENT_TYPES });
  }

  const message = props.defaultPrompt ?? visionAiStore.defaultPrompts[0];
  if (checkIfInvalidThreshold(props.intentTypeOverride ?? visionAiStore.currentIntentType)) {
    await visionAiStore.createMockMessages({});
  } else {
    if (props.intentTypeOverride === INTENT_TYPES.UNIFIED) {
      visionAiStore.startNewChat();
    }
    await visionAiStore.startConversation({
      message,
      buttonIdentifier: props.buttonIdentifier,
      extra: props.extra,
      intentTypeOverride: props.intentTypeOverride,
      filters: pageFilters.value,
    });
  }

  emit('openChatWindow', event);
  aiSummaryLaunchEvent({
    launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.ICON,
    intentType: props.intentTypeOverride ?? visionAiStore.currentIntentType,
  });
}

const buttonSize = computed(() => (props.large ? 'lg' : 'sm'));
</script>

<template>
  <div data-cy="vision-ai-button" :class="['vision-ai-button', 'dash-transition-md', '!left-5']">
    <DashingVisionAiButton
      v-dash-tooltip="tooltip"
      :size="buttonSize"
      :collapsible="true"
      :loading="visionAiMessagesStore.summaryLoading"
      :disabled="visionAiMessagesStore.summaryLoading"
      severity="visionAI"
      :label="label"
      @click="handleButtonClicked($event)"
    />
  </div>
</template>
